import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';

import { UserService } from '@intranet/modules/users/user.service';

import { ConfirmDialogData } from '@intranet/components/dialog/confirm-dialog/confirm-dialog.component';
import { switchMap } from 'rxjs';
import {
    User,
    UserDetailResult,
    UserGroup,
    UserPriceBand,
    UserRoleMember,
} from '@intranet/modules/users/user.types';
import { LoginService } from '@intranet/modules/login';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import * as moment from 'moment';

@Component({
    selector: 'admin-user',
    templateUrl: './admin-user-edit.component.html',
    styleUrls: ['./admin-user-edit.component.scss'],
})
export class AdminUserEditComponent implements OnInit {
    @Input() user: User | null;
    @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() update: EventEmitter<User> = new EventEmitter<User>();
    @ViewChild('form') userForm: NgForm;

    title = '';
    isLoading: boolean = false;
    userGroups: UserGroup[];
    memberOfGroup: UserGroup;
    roles: UserRoleMember[];
    oldpassword: string;
    newpassword: string;
    confirmpassword: string;
    showChangePassword: boolean;
    error: string | null;
    isCreate: boolean = false;
    confirmDialog: ConfirmDialogData = new ConfirmDialogData();
    priceBandName: string;
    priceBandStartDate: Date;
    priceBands: string[] = [];
    priceBandHistory: UserPriceBand[] = [];
    showPriceBandHistory: boolean = false;
    priceBandDeleting: { [key: string]: boolean } = {};
    isSubmitting: boolean = false;
    isEditingPriceBand: boolean = false;

    ngOnInit(): void {
        // Register hotkey for "Cancel" action (Ctrl + Q)
        this.hotkeyService.registerHotkey('ctrl+q', 'Cancel action', () => this.onCancel());
        // Register hotkey for "Save Changes" or "Add User" action (Ctrl + Alt + S)
        this.hotkeyService.registerHotkey('ctrl+alt+s', 'Save changes', () => {
            if (!this.showChangePassword) {
                // Trigger save if the form is valid and not pristine
                if (this.userForm?.valid && !this.userForm?.pristine) {
                    this.onSubmit();
                }
            } else {
                // Trigger password update if both passwords are valid
                if (this.newpassword && this.confirmpassword && this.newpassword === this.confirmpassword) {
                    this.updatePassword();
                }
            }
        });

        this.route.paramMap
            .pipe(
                switchMap((p) => {
                    let userId = p.get('id') || this.user?.id;
                    if (!userId) {
                        this.title = 'Add user';
                        return this.userService.initNewUser();
                    }

                    this.title = 'Edit user';
                    return this.userService.getUserDetails(userId);
                })
            )
            .subscribe({
                next: (r) => {
                    this.updateFromDetails(r);
                },
                error: (error) => {
                    this.error = error;
                },
            });
    }

    constructor(
        private userService: UserService,
        private router: Router,
        private route: ActivatedRoute,
        public loginService: LoginService,
        private hotkeyService: HotkeyService
    ) {}

    private updateFromDetails(r: UserDetailResult) {
        this.user = r.user;
        this.userGroups = r.userGroups;
        this.memberOfGroup = this.userGroups[0];
        this.roles = r.roleMembers;
        if (this.user.userGroup) {
            var curGroup = r.user.userGroup;
            var group = this.userGroups.find(
                (u) => u.userGroupId === curGroup.userGroupId
            );
            if (group) this.memberOfGroup = group;
        }
        this.isCreate = r.isCreate;
        this.priceBandName = r.priceBandName ?? '';
        this.priceBands = r.priceBands;
        this.priceBandHistory = r.priceBandHistory;
        this.priceBandStartDate = new Date();
        let today = moment().startOf('day').add(1, 'day').toDate();
        this.priceBandStartDate = today;
        this.refreshCurrentPriceBand();
    }

    setChangePassword() {
        this.showChangePassword = true;
    }

    cancelChangePassword() {
        this.showChangePassword = false;
    }

    updatePassword() {
        if (this.user) {
            this.userService
                .updateUserPassword(
                    this.user.id,
                    this.newpassword,
                    this.confirmpassword
                )
                .subscribe((r) => {
                    if (!r.success) {
                        this.error = r.error;
                    } else {
                        this.router.navigate(['/admin/users']);
                    }
                });
        }
    }

    toggleEditPriceBand() {
        this.isEditingPriceBand = !this.isEditingPriceBand;
    }

    deleteUserPriceBand(priceBand: UserPriceBand) {
        // Use a unique key based on priceBandName and start date
        const uniqueKey = `${priceBand.priceBandName}_${priceBand.start}`;

        // Set loading state only for this specific price band
        this.priceBandDeleting[uniqueKey] = true;

        this.userService.deleteUserPriceBand(this.user.id, priceBand.priceBandName, priceBand.start).subscribe({
            next: (r) => {
                if (r.success) {
                    // Immediately remove the deleted band from the list
                    this.priceBandHistory = this.priceBandHistory.filter(band =>
                        !(band.priceBandName === priceBand.priceBandName && band.start === priceBand.start)
                    );
                    this.refreshCurrentPriceBand();
                    delete this.priceBandDeleting[uniqueKey];
                } else {
                    this.error = r.error;
                    delete this.priceBandDeleting[uniqueKey];
                }
            },
            error: (error) => {
                this.error = error.error;
                delete this.priceBandDeleting[uniqueKey];
            }
        });
    }

    clearUserAssociation() {
        if (this.user) {
            var targetUser = this.user;
            this.confirmDialog = new ConfirmDialogData(
                true,
                'Remove Association for User',
                "Are you sure you want to remove this user's association?",
                'Remove Association',
                () => {
                    this.confirmDeleteAssociation(targetUser);
                },
                () => {
                    this.cancelDeleteAssociation();
                }
            );
        }
    }

    confirmDeleteAssociation(user: User) {
        this.userService.removeUserAssociation(user).subscribe(
            (r) => {
                if (r.success) {
                    if (this.user) {
                        this.user.transferPassword = undefined;
                        this.confirmDialog = new ConfirmDialogData();
                    }
                }
            },
            (error) => (this.error = error)
        );
    }

    cancelDeleteAssociation() {
        this.confirmDialog = new ConfirmDialogData();
    }

    onSubmit() {
        if (!this.user) {
            return;
        }

        this.isSubmitting = true;

        this.user.userGroupId = this.memberOfGroup.userGroupId;

        if (this.isCreate) {
            this.user.userName = this.user.email;
        }

        const result$ = this.isCreate
            ? this.userService.addNewUser(
                  this.user,
                  this.newpassword,
                  this.confirmpassword,
                  this.roles,
                  this.priceBandName
              )
            : this.userService.updateUser(this.user, this.roles, this.priceBandName, this.priceBandStartDate);

        result$.subscribe({
                next: (res) => {
                    this.isSubmitting = false;
                    if (!res.success) {
                        this.error = res.error;
                    } else {
                        this.update.emit(this.user);
                        this.closeDialog();
                    }
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.error = error;
                }
            }
        );
    }


    onCancel() {
        this.closeDialog();
    }

    closeDialog() {
        this.close.emit(false);
    }

    displayEndDate(priceBand: UserPriceBand): string {
        let enddate = moment(priceBand.end);
        if (enddate.year() > 4000) {
            return "No end date";
        }
        return enddate.add(-1, 'day').format('DD MMM YYYY');
    }

      private refreshCurrentPriceBand() {
        if (!this.priceBandHistory.length) {
            this.priceBandName = '';
            this.priceBandStartDate = moment().startOf('day').add(1, 'day').toDate();
            return;
        }

        // Sort history by start date (ascending)
        this.priceBandHistory.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

        // Set all bands to have proper end dates
        for (let i = 0; i < this.priceBandHistory.length; i++) {
            const current = this.priceBandHistory[i];
            const next = this.priceBandHistory[i + 1];

            if (next) {
                // Set current's end date to next's start date
                current.end = next.start;
            } else {
                // This is the most recent one → current band
                current.end = new Date(4001, 0, 1); // simulate an infinite or ongoing date
                this.priceBandName = current.priceBandName;
                this.priceBandStartDate = new Date(current.start);
            }
        }
    }
}
